import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { misc, loyaltyPrograms, files as filesApi } from '../services/api';
import { Award, Gift, Ticket, Star, Loader2, Building2, ChevronRight, QrCode, CheckCircle2, FileText, ExternalLink, X } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import CategoryChips from '../components/CategoryChips';
import ClampWithMore from '../components/ClampWithMore';
import TermsContent from '../components/TermsContent';
import SiteSections from '../components/SiteSections';

const ICONS = { Ticket, Gift, Star, Award };

export default function Loyalty() {
  const { user, setUserData, refresh } = useAuth();
  const [rewards, setRewards] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState('');
  const [cat, setCat] = useState('All');
  const [showTerms, setShowTerms] = useState(false);

  const filteredPrograms = useMemo(() =>
    cat === 'All' ? programs : programs.filter(p => p.businessCategory === cat),
  [programs, cat]);
  const programCounts = useMemo(() => {
    const m = { All: programs.length };
    programs.forEach(p => { if (p.businessCategory) m[p.businessCategory] = (m[p.businessCategory] || 0) + 1; });
    return m;
  }, [programs]);

  useEffect(() => {
    Promise.all([
      misc.rewards(),
      loyaltyPrograms.directory().catch(() => []),
      loyaltyPrograms.myCards().catch(() => []),
    ]).then(([r, p, c]) => {
      setRewards(r);
      setPrograms(p);
      setCards(c);
    }).finally(() => setLoading(false));
  }, []);

  const redeem = async (r) => {
    if ((user?.points || 0) < r.cost) { toast({ title:'Not enough points', description:`You need ${r.cost - user.points} more points.` }); return; }
    try {
      const u = await misc.redeemReward(r.id);
      setUserData(u);
      toast({ title:'Reward redeemed!', description:`${r.name} added to your rewards.` });
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' });
    }
  };

  const redeemStampCard = async (card) => {
    setRedeeming(card.id);
    try {
      const res = await loyaltyPrograms.redeemCard(card.id);
      toast({ title: 'Reward unlocked!', description: `Show code ${res.redemptionCode} at ${card.businessName}.` });
      const fresh = await loyaltyPrograms.myCards();
      setCards(fresh);
    } catch (err) {
      toast({ title: 'Redeem failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setRedeeming(''); }
  };

  const nextTier = 5000;
  const tierPct = Math.min(100, ((user?.points||0) / nextTier) * 100);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold">Loyalty & Points</h1>
        <p className="text-gray-400 text-sm">Earn at every business. Redeem for rewards. Level up your tier.</p>
      </div>
      <div className="pbb-card p-6 flex flex-col md:flex-row items-start md:items-center gap-6">
        <div className="w-24 h-24 rounded-full bg-green-600/15 border border-green-600/40 flex items-center justify-center">
          <Award className="text-green-400" size={40} />
        </div>
        <div className="flex-1 w-full">
          <div className="text-sm text-gray-400">Your balance</div>
          <div className="text-4xl font-bold text-green-400">{user?.points?.toLocaleString()} pts</div>
          <div className="text-xs text-gray-400 mt-1">Tier: {user?.tier} • {Math.max(0, nextTier - (user?.points||0))} pts to next tier</div>
          <div className="h-2 mt-3 rounded-full bg-[#111] overflow-hidden">
            <div className="h-full bg-green-500" style={{ width: `${tierPct}%` }} />
          </div>
        </div>
      </div>
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><QrCode size={18} className="text-green-400"/> My Stamp Cards</h2>
        {loading ? <Loader2 className="animate-spin text-gray-400" size={16}/> : cards.length === 0 ? (
          <div data-testid="stamp-cards-empty" className="pbb-card p-6 text-center">
            <QrCode className="mx-auto text-green-400" size={24}/>
            <div className="mt-2 text-sm text-gray-300">No stamp cards yet — scan a business's QR code in-store to start collecting.</div>
          </div>
        ) : (
          <div data-testid="stamp-cards" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {cards.map(c => {
              const total = c.visits_required || 10;
              const stamps = c.stamps || 0;
              const full = stamps >= total;
              return (
                <div key={c.id} data-testid={`stamp-card-${c.id}`} className={`pbb-card p-4 ${c.redeemed ? 'opacity-60' : ''}`}>
                  <div className="flex items-center gap-3">
                    {c.businessImage ? <img src={c.businessImage} alt="" className="w-10 h-10 rounded-lg object-cover"/> : <div className="w-10 h-10 rounded-lg bg-[#0d1f14] border border-[#153b23] flex items-center justify-center"><Building2 size={16} className="text-gray-500"/></div>}
                    <div className="flex-1 min-w-0">
                      <Link to={`/business/${c.businessId}`} className="font-semibold text-sm truncate block hover:text-green-400">{c.businessName}</Link>
                      <div className="text-xs text-gray-400 truncate">{c.programName}</div>
                    </div>
                    <div className="text-xs text-green-400 font-semibold">{stamps}/{total}</div>
                  </div>
                  <div className="grid grid-cols-5 sm:grid-cols-10 gap-1 mt-3">
                    {Array.from({ length: total }).map((_, i) => (
                      <div key={i} className={`aspect-square rounded-md border ${i < stamps ? 'bg-green-500/25 border-green-400 text-green-300 shadow-[0_0_0_1px_rgba(74,222,128,0.35)]' : 'bg-[#1a2a20] border-dashed border-green-600/40 text-gray-500'} flex items-center justify-center text-xs font-bold`}>
                        {i < stamps ? '★' : '·'}
                      </div>
                    ))}
                  </div>
                  <ClampWithMore text={c.reward} lines={3} className="text-[11px] text-gray-500 mt-2" testId={`stamp-reward-${c.id}`} moreLabel="Read more" lessLabel="Show less" />
                  {c.redeemed ? (
                    <div className="mt-3 text-xs text-gray-400 inline-flex items-center gap-1"><CheckCircle2 size={12} className="text-green-400"/> Redeemed · code {c.redemptionCode}</div>
                  ) : full ? (
                    <button
                      onClick={() => redeemStampCard(c)}
                      disabled={redeeming === c.id}
                      data-testid={`stamp-card-redeem-${c.id}`}
                      className="pbb-btn-green mt-3 inline-flex items-center justify-center gap-1 w-full disabled:opacity-60"
                    >
                      {redeeming === c.id ? <Loader2 size={14} className="animate-spin"/> : <Gift size={14}/>} Redeem reward
                    </button>
                  ) : (
                    <div className="mt-3 text-[11px] text-gray-500">{total - stamps} more visits to unlock</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Business Loyalty Programs</h2>
        <div className="mb-3"><CategoryChips value={cat} onChange={setCat} counts={programCounts} testId="loyalty-cats" /></div>
        {loading ? <Loader2 className="animate-spin text-gray-400" size={16}/> : filteredPrograms.length === 0 ? (
          <div data-testid="loyalty-directory-empty" className="pbb-card p-6 text-sm text-gray-400 text-center">No business loyalty programs in <b>{cat}</b> yet — check back soon.</div>
        ) : (
          <div data-testid="loyalty-directory" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPrograms.map((p) => (
              <Link to={`/business/${p.businessId}`} key={p.id} data-testid={`loyalty-directory-${p.id}`} className="pbb-card overflow-hidden hover:border-green-500/50 transition-colors group">
                <div className="w-full aspect-[16/9] bg-[#0d1f14] overflow-hidden relative">
                  {p.businessImage
                    ? <img src={filesApi.urlFor(p.businessImage)} alt={p.businessName} className="absolute inset-0 w-full h-full object-cover object-center" />
                    : <div className="absolute inset-0 flex items-center justify-center text-gray-600"><Building2 size={28}/></div>}
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 border border-green-500/30 text-[10px] font-semibold text-green-400 uppercase tracking-wider">
                    <Award size={10}/> {p.businessPackage || 'basic'}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">{p.businessCategory || 'Business'}</div>
                  <div className="font-semibold text-sm text-white mt-0.5 truncate">{p.businessName}</div>
                  <div className="mt-2 rounded-lg bg-[#0d1f14] border border-[#153b23] px-3 py-2">
                    <div className="text-xs font-semibold text-green-300">{p.name}</div>
                    <ClampWithMore text={p.reward} lines={3} className="text-[11px] text-gray-400 mt-0.5" testId={`loyalty-reward-${p.id}`} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-gray-400">
                    {p.visits_required ? <span>🎯 {p.visits_required} visits</span> : null}
                    {p.points_per_visit ? <span>💚 +{p.points_per_visit}/visit</span> : null}
                    {p.points_to_redeem ? <span>🎁 {p.points_to_redeem} pts</span> : null}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-green-400 group-hover:text-green-300">
                    View business <ChevronRight size={11}/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Redeem Points</h2>
        {loading ? <Loader2 className="animate-spin text-gray-400" size={16}/> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {rewards.map(r => {
              const Ic = ICONS[r.icon] || Award;
              return (
                <div key={r.id} className="pbb-card p-5">
                  <Ic className="text-green-400 mb-2" size={22}/>
                  <div className="font-semibold">{r.name}</div>
                  <div className="text-sm text-gray-400 mt-1">{r.cost.toLocaleString()} pts</div>
                  <button onClick={() => redeem(r)} className="pbb-btn-green mt-3">Redeem</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="pbb-card p-5">
        <h3 className="font-semibold mb-2">How to earn</h3>
        <ul className="text-sm text-gray-300 space-y-1 list-disc pl-5">
          <li>1,000 points welcome bonus (already added 🎉)</li>
          <li>Redeem business coupons: +100 to +1000 pts <span className="text-green-400">(+10 on PBB Loyalty Card)</span></li>
          <li>Shop the online store / auction: 10 pts per $1 <span className="text-green-400">(+10 on PBB Loyalty Card)</span></li>
          <li>Attend events: +250 pts / RSVP <span className="text-green-400">(+10 on PBB Loyalty Card)</span></li>
          <li>Scan a local merchant's QR code in-store → +1 stamp toward that business's reward</li>
          <li>Fill your PBB Loyalty Card (100 pts) → auto +50 loyalty points</li>
        </ul>
        <div className="mt-4 pt-4 border-t border-[#153b23] flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400">
          <FileText size={12} className="text-green-400 shrink-0" />
          <span>By enrolling and earning points, you agree to the</span>
          <button
            type="button"
            onClick={() => setShowTerms(true)}
            data-testid="loyalty-terms-link"
            className="text-green-400 hover:text-green-300 underline underline-offset-2 font-medium"
          >
            Loyalty Program Terms &amp; Conditions
          </button>
          <span>.</span>
        </div>
      </div>

      {showTerms && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="terms-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) setShowTerms(false); }}
          data-testid="loyalty-terms-modal"
        >
          <div className="pbb-card w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-[#153b23] shrink-0">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-green-400" />
                <h2 id="terms-modal-title" className="font-semibold text-white">Terms &amp; Conditions</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                data-testid="loyalty-terms-modal-close"
                className="p-1 rounded-md hover:bg-[#0d1f14] text-gray-400 hover:text-white"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <TermsContent />
            </div>
            <div className="flex items-center justify-between gap-3 p-4 border-t border-[#153b23] shrink-0">
              <Link
                to="/terms"
                onClick={() => setShowTerms(false)}
                data-testid="loyalty-terms-view-full"
                className="inline-flex items-center gap-1 text-xs text-green-400 hover:text-green-300"
              >
                View full page <ExternalLink size={12} />
              </Link>
              <button
                type="button"
                onClick={() => setShowTerms(false)}
                data-testid="loyalty-terms-modal-done"
                className="pbb-btn-green text-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      <SiteSections pageKey="loyalty" />
    </div>
  );
}
